import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../services/api'
import { useAuthStore } from '../stores/auth'
import { Icon } from '../components/common'
import { homePath } from '../utils/navigation'
import type { LoginResponse } from '../types'

// The three public account pages reached from an email: forgot password,
// reset password (with token) and accept invitation (with token).

function errorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'message' in err) return (err as { message: string }).message
  return fallback
}

function Card({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <Icon name="cloud" size={28} />
          </div>
          <h1 className="login-title">{title}</h1>
          <p className="login-subtitle">{subtitle}</p>
        </div>
        {children}
        <div style={{ textAlign: 'center', marginTop: 'var(--space-6)', fontSize: 'var(--text-sm)' }}>
          <Link to="/login">Back to sign in</Link>
        </div>
      </div>
    </div>
  )
}

export function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setPending(true)
    try {
      await api.post('/auth/forgot', { email: email.trim() })
      setSent(true)
      setError(null)
    } catch (err) {
      setError(errorMessage(err, 'Could not send the reset link. Try again.'))
    } finally {
      setPending(false)
    }
  }

  return (
    <Card title="Forgot password" subtitle="We will email you a link to choose a new one">
      {sent ? (
        <div className="login-info show" role="status">
          If that address has an account, a reset link is on its way. It expires in one hour.
        </div>
      ) : (
        <form className="login-form" onSubmit={submit}>
          {error && <div className="login-error show" role="alert">{error}</div>}
          <div className="form-group">
            <label className="form-label" htmlFor="forgot-email">Email</label>
            <input id="forgot-email" type="email" className="form-input" required autoComplete="email"
              value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" disabled={pending} />
          </div>
          <button type="submit" className="btn btn-primary" disabled={pending}>
            {pending ? <span className="loading-spinner sm" /> : 'Send reset link'}
          </button>
        </form>
      )}
    </Card>
  )
}

// SetPasswordForm posts {token, password} to the given endpoint.
function SetPasswordForm({ endpoint, submitText, onDone }: { endpoint: string; submitText: string; onDone: (res: LoginResponse | null) => void }) {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (password.length < 8) return setError('Password must be at least 8 characters')
    if (password !== confirm) return setError('Passwords do not match')
    setPending(true)
    try {
      const res = await api.post<LoginResponse | { status: string }>(endpoint, { token, password })
      onDone('access_token' in res ? res : null)
    } catch (err) {
      setError(errorMessage(err, 'This link is invalid or has expired.'))
    } finally {
      setPending(false)
    }
  }

  if (!token) {
    return <div className="login-error show" role="alert">This link is missing its token. Open the link from your email again.</div>
  }
  return (
    <form className="login-form" onSubmit={submit}>
      {error && <div className="login-error show" role="alert">{error}</div>}
      <div className="form-group">
        <label className="form-label" htmlFor="set-password">New password</label>
        <input id="set-password" type="password" className="form-input" required autoComplete="new-password"
          value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimum 8 characters" disabled={pending} />
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="set-password-confirm">Confirm password</label>
        <input id="set-password-confirm" type="password" className="form-input" required autoComplete="new-password"
          value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat the password" disabled={pending} />
      </div>
      <button type="submit" className="btn btn-primary" disabled={pending}>
        {pending ? <span className="loading-spinner sm" /> : submitText}
      </button>
    </form>
  )
}

export function ResetPassword() {
  const navigate = useNavigate()
  const [done, setDone] = useState(false)
  return (
    <Card title="Choose a new password" subtitle="Every other session will be signed out">
      {done ? (
        <div className="login-info show" role="status">
          Password updated. <button type="button" className="btn btn-primary" style={{ marginTop: '12px' }} onClick={() => navigate('/login')}>Sign in</button>
        </div>
      ) : (
        <SetPasswordForm endpoint="/auth/reset" submitText="Update password" onDone={() => setDone(true)} />
      )}
    </Card>
  )
}

export function AcceptInvite() {
  const navigate = useNavigate()
  const { setTokens, setUser } = useAuthStore()
  return (
    <Card title="Welcome to Sentyr Cloud" subtitle="Choose the password for your new account">
      <SetPasswordForm endpoint="/auth/accept-invite" submitText="Create password and sign in" onDone={(res) => {
        if (res) {
          setTokens(res.access_token, res.refresh_token)
          setUser(res.user)
        }
        navigate(homePath(res?.user.role), { replace: true })
      }} />
    </Card>
  )
}
